/**
 * @fileoverview Timezone conversion utilities for LOCI events
 *
 * Provides functions for converting event times between timezones,
 * handling DST transitions, and formatting times for API responses.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */

import { format, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

/**
 * Supported IANA timezone identifiers
 * Common US timezones for LOCI launch markets
 */
export const SUPPORTED_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Honolulu',
  'UTC',
] as const;

export type SupportedTimezone = (typeof SUPPORTED_TIMEZONES)[number];

/**
 * Validates if a string is a valid IANA timezone identifier
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    // Try to format a date in the timezone - if it throws, it's invalid
    formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
    return true;
  } catch {
    return false;
  }
}

/**
 * Time format representation for API responses
 */
export interface TimeFormats {
  /** UTC timestamp in ISO 8601 format */
  utc: string;
  /** Local time in ISO 8601 format (venue timezone) */
  local: string;
  /** Local date in YYYY-MM-DD format */
  localDate: string;
  /** Local time in HH:mm format (24-hour) */
  localTime: string;
  /** IANA timezone identifier */
  timezone: string;
  /** Human-readable timezone abbreviation (e.g., "EST", "PDT") */
  timezoneAbbr: string;
  /** UTC offset in format "+HH:mm" or "-HH:mm" */
  utcOffset: string;
}

/**
 * Convert a date to multiple format representations for API responses
 *
 * @param date - Date object or ISO string to convert
 * @param timezone - IANA timezone identifier (defaults to UTC)
 * @returns TimeFormats object with multiple representations
 *
 * @example
 * ```typescript
 * const formats = formatEventTime(
 *   new Date('2026-02-15T20:00:00Z'),
 *   'America/New_York'
 * );
 * // {
 * //   utc: '2026-02-15T20:00:00.000Z',
 * //   local: '2026-02-15T15:00:00-05:00',
 * //   localDate: '2026-02-15',
 * //   localTime: '15:00',
 * //   timezone: 'America/New_York',
 * //   timezoneAbbr: 'EST',
 * //   utcOffset: '-05:00'
 * // }
 * ```
 */
export function formatEventTime(date: Date | string, timezone: string = 'UTC'): TimeFormats {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  // Validate timezone
  if (!isValidTimezone(timezone)) {
    console.warn(`Invalid timezone: ${timezone}, falling back to UTC`);
    timezone = 'UTC';
  }

  // Get UTC representation
  const utc = dateObj.toISOString();

  // Get local time in the specified timezone
  const local = formatInTimeZone(dateObj, timezone, "yyyy-MM-dd'T'HH:mm:ssXXX");
  const localDate = formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd');
  const localTime = formatInTimeZone(dateObj, timezone, 'HH:mm');

  // Get timezone abbreviation (e.g., EST, PDT)
  const timezoneAbbr = formatInTimeZone(dateObj, timezone, 'zzz');

  // Get UTC offset
  const utcOffset = formatInTimeZone(dateObj, timezone, 'XXX');

  return {
    utc,
    local,
    localDate,
    localTime,
    timezone,
    timezoneAbbr,
    utcOffset,
  };
}

/**
 * Extended time formats for event date/time pairs
 */
export interface EventTimeFormats {
  /** Start time in multiple formats */
  startTime: TimeFormats;
  /** End time in multiple formats (if provided) */
  endTime?: TimeFormats;
  /** Duration in minutes (if end time provided) */
  durationMinutes?: number;
  /** Whether the event spans multiple days in local time */
  isMultiDay: boolean;
  /** Whether DST transition occurs during event */
  crossesDSTBoundary?: boolean;
}

/**
 * Format event start and end times with timezone awareness
 *
 * Handles multi-day events and DST transitions
 *
 * @param startDate - Event start date
 * @param endDate - Event end date (optional)
 * @param timezone - IANA timezone identifier
 * @returns EventTimeFormats with comprehensive time information
 *
 * @example
 * ```typescript
 * const times = formatEventTimes(
 *   new Date('2026-03-08T02:00:00Z'), // DST starts
 *   new Date('2026-03-08T05:00:00Z'),
 *   'America/New_York'
 * );
 * ```
 */
export function formatEventTimes(
  startDate: Date | string,
  endDate: Date | string | null | undefined,
  timezone: string = 'UTC',
): EventTimeFormats {
  const startDateObj = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const endDateObj = endDate
    ? typeof endDate === 'string'
      ? parseISO(endDate)
      : endDate
    : undefined;

  const startTime = formatEventTime(startDateObj, timezone);
  const endTime = endDateObj ? formatEventTime(endDateObj, timezone) : undefined;

  // Calculate duration if end time provided
  let durationMinutes: number | undefined;
  if (endDateObj) {
    durationMinutes = Math.round((endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60));
  }

  // Check if event spans multiple days in local timezone
  const isMultiDay = endTime ? startTime.localDate !== endTime.localDate : false;

  // Check if DST boundary is crossed
  let crossesDSTBoundary: boolean | undefined;
  if (endTime) {
    // DST boundary is crossed if UTC offsets differ
    crossesDSTBoundary = startTime.utcOffset !== endTime.utcOffset;
  }

  return {
    startTime,
    endTime,
    durationMinutes,
    isMultiDay,
    crossesDSTBoundary,
  };
}

/**
 * Convert local time to UTC for database storage
 *
 * @param localTime - Local time as Date or ISO string
 * @param timezone - IANA timezone identifier
 * @returns UTC Date object
 *
 * @example
 * ```typescript
 * // Event at 8pm EST
 * const utcDate = localToUTC(
 *   new Date('2026-02-15T20:00:00'),
 *   'America/New_York'
 * );
 * // Returns Date representing 2026-02-16T01:00:00.000Z
 * ```
 */
export function localToUTC(localTime: Date | string, timezone: string): Date {
  const localDate = typeof localTime === 'string' ? parseISO(localTime) : localTime;

  if (!isValidTimezone(timezone)) {
    console.warn(`Invalid timezone: ${timezone}, treating as UTC`);
    return localDate;
  }

  // Convert from local time in specified timezone to UTC
  return fromZonedTime(localDate, timezone);
}

/**
 * Convert UTC time to local time in specified timezone
 *
 * @param utcTime - UTC time as Date or ISO string
 * @param timezone - IANA timezone identifier
 * @returns Date object in local timezone
 *
 * @example
 * ```typescript
 * const localDate = utcToLocal(
 *   new Date('2026-02-16T01:00:00.000Z'),
 *   'America/New_York'
 * );
 * // Returns Date representing 8pm on Feb 15 in EST
 * ```
 */
export function utcToLocal(utcTime: Date | string, timezone: string): Date {
  const utcDate = typeof utcTime === 'string' ? parseISO(utcTime) : utcTime;

  if (!isValidTimezone(timezone)) {
    console.warn(`Invalid timezone: ${timezone}, returning UTC`);
    return utcDate;
  }

  // Convert from UTC to local time in specified timezone
  return toZonedTime(utcDate, timezone);
}

/**
 * Get the current UTC offset for a timezone
 * Handles DST automatically
 *
 * NOTE: This returns the number of minutes that the timezone is AHEAD of UTC,
 * which is the inverse of the standard timezone offset definition.
 * This is used internally for DST detection comparisons.
 * For the standard UTC offset string (e.g., "-05:00"), use formatEventTime().utcOffset.
 *
 * @param timezone - IANA timezone identifier
 * @param date - Date to check offset for (defaults to now)
 * @returns Offset in minutes (positive = ahead of UTC, negative = behind UTC)
 */
export function getTimezoneOffset(timezone: string, date: Date = new Date()): number {
  const utcDate = date.getTime();
  const localDate = toZonedTime(date, timezone).getTime();
  return Math.round((localDate - utcDate) / (1000 * 60));
}

/**
 * Check if DST is currently active in a timezone
 *
 * @param timezone - IANA timezone identifier
 * @param date - Date to check (defaults to now)
 * @returns True if DST is active
 */
export function isDSTActive(timezone: string, date: Date = new Date()): boolean {
  // Get offset in January (winter) and July (summer)
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);

  const janOffset = getTimezoneOffset(timezone, jan);
  const julOffset = getTimezoneOffset(timezone, jul);
  const currentOffset = getTimezoneOffset(timezone, date);

  // DST is active if current offset equals the larger offset
  const maxOffset = Math.max(janOffset, julOffset);
  return currentOffset === maxOffset;
}

/**
 * Format a date range for display
 *
 * @param startDate - Event start date
 * @param endDate - Event end date (optional)
 * @param timezone - IANA timezone identifier
 * @returns Human-readable date range string
 *
 * @example
 * ```typescript
 * formatDateRange(
 *   new Date('2026-02-15T20:00:00Z'),
 *   new Date('2026-02-15T23:00:00Z'),
 *   'America/New_York'
 * );
 * // Returns: "Feb 15, 2026, 3:00 PM - 6:00 PM EST"
 *
 * formatDateRange(
 *   new Date('2026-02-15T20:00:00Z'),
 *   new Date('2026-02-16T20:00:00Z'),
 *   'America/New_York'
 * );
 * // Returns: "Feb 15, 2026, 3:00 PM - Feb 16, 2026, 3:00 PM EST"
 * ```
 */
export function formatDateRange(
  startDate: Date | string,
  endDate: Date | string | null | undefined,
  timezone: string = 'UTC',
): string {
  const startDateObj = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const endDateObj = endDate
    ? typeof endDate === 'string'
      ? parseISO(endDate)
      : endDate
    : undefined;

  const startFormatted = formatInTimeZone(startDateObj, timezone, 'MMM d, yyyy, h:mm a');

  if (!endDateObj) {
    const tz = formatInTimeZone(startDateObj, timezone, 'zzz');
    return `${startFormatted} ${tz}`;
  }

  const startDateOnly = formatInTimeZone(startDateObj, timezone, 'yyyy-MM-dd');
  const endDateOnly = formatInTimeZone(endDateObj, timezone, 'yyyy-MM-dd');
  const tz = formatInTimeZone(endDateObj, timezone, 'zzz');

  if (startDateOnly === endDateOnly) {
    // Same day - show time range
    const endTime = formatInTimeZone(endDateObj, timezone, 'h:mm a');
    return `${startFormatted} - ${endTime} ${tz}`;
  } else {
    // Different days - show full date range
    const endFormatted = formatInTimeZone(endDateObj, timezone, 'MMM d, yyyy, h:mm a');
    return `${startFormatted} - ${endFormatted} ${tz}`;
  }
}

/**
 * Format an event start time as a single humanized line for cards and rails.
 *
 * Pure presentational helper — accepts the canonical wire-format `startsAt`
 * (`Date | string`) and an optional IANA timezone, and returns a string like
 * `"Wed, May 20 · 7:00 PM EDT"`. Used by every card/rail surface that
 * historically rendered raw ISO strings (WEB-272).
 *
 * Invariants:
 * - When `startsAt` cannot be parsed, returns an empty string so callers can
 *   omit the subtitle row rather than leaking `2026-10-16T00:00:00.000Z`
 *   style raw ISO.
 * - When `timezone` is invalid, falls back to `UTC` (so a valid `startsAt`
 *   still produces a humanized label, just rendered in UTC).
 * - The output **never** matches `/^\d{4}-\d{2}-\d{2}T/` for any valid date —
 *   guarded by the assertion in `timezone.test.ts`. Card surfaces depend on
 *   this contract for the WEB-272 acceptance criteria.
 *
 * @param startsAt - Event start as a `Date` or ISO 8601 string
 * @param timezone - IANA timezone (e.g. `America/New_York`); defaults to UTC
 * @returns Humanized single-line label, or `''` when the input is unparseable
 *
 * @example
 * ```typescript
 * formatEventDateTime('2026-05-20T19:00:00-04:00', 'America/New_York');
 * // → 'Wed, May 20 · 7:00 PM EDT'
 * ```
 */
export function formatEventDateTime(date: Date | string, timezone: string = 'UTC'): string {
  let dateObj: Date;
  try {
    dateObj = typeof date === 'string' ? parseISO(date) : date;
  } catch {
    return '';
  }
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
    return '';
  }

  const tz = isValidTimezone(timezone) ? timezone : 'UTC';

  try {
    // `EEE, MMM d · h:mm a zzz` — e.g. "Wed, May 20 · 7:00 PM EDT".
    // The `·` separator is the same glyph used elsewhere in the card meta
    // grammar (DiscoverEventCard, MapSheetRow) so output reads consistently.
    return formatInTimeZone(dateObj, tz, 'EEE, MMM d · h:mm a zzz');
  } catch {
    return '';
  }
}

/** Internal: parse a `Date | string` into a valid `Date`, or `null`. */
function toValidDate(value: Date | string): Date | null {
  let dateObj: Date;
  try {
    dateObj = typeof value === 'string' ? parseISO(value) : value;
  } catch {
    return null;
  }
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
    return null;
  }
  return dateObj;
}

/**
 * Format an event start/end pair as an EXPLICIT single-line range.
 *
 * Sibling to {@link formatEventDateTime} — same date-fns/`formatInTimeZone`
 * helpers, same `·` separator grammar, same fail-soft contract — but renders
 * a clear two-sided range instead of opaque arrow notation like
 * `"12 PM · → MAY 17"` (WEB-293, audit item 25). Each side always carries
 * both the day-of-week **and** the calendar date so a reader instantly knows
 * the event *spans* those days rather than guessing starts/ends.
 *
 * Format:
 * - **Multi-day:** `"Sat, May 16 · 12:00 PM → Sun, May 17 · 6:00 PM EDT"`
 *   (weekday + date + time on each side; single trailing tz abbreviation).
 * - **Same local day:** collapses to a single-day time range —
 *   `"Sat, May 16 · 12:00 PM – 6:00 PM EDT"` (en-dash, no repeated date).
 * - **No end provided:** delegates to {@link formatEventDateTime} (single
 *   instant), so callers can pass a possibly-undefined `endsAt` without a
 *   branch.
 *
 * Invariants (mirrors `formatEventDateTime`):
 * - Unparseable `start` → `''` so the caller can omit the row rather than
 *   leak raw ISO. Unparseable `end` → falls back to the single-instant
 *   `formatEventDateTime(start, …)` rather than dropping the start entirely.
 * - Invalid `timezone` → falls back to `UTC`.
 * - Output never matches `/^\d{4}-\d{2}-\d{2}T/` for any valid input.
 *
 * @param start - Event start as a `Date` or ISO 8601 string
 * @param end - Event end as a `Date`/ISO string, or `null`/`undefined`
 * @param timezone - IANA timezone (e.g. `America/New_York`); defaults to UTC
 * @returns Humanized single-line range, or `''` when `start` is unparseable
 *
 * @example
 * ```typescript
 * formatEventDateRange(
 *   '2026-05-16T12:00:00-04:00',
 *   '2026-05-17T18:00:00-04:00',
 *   'America/New_York',
 * );
 * // → 'Sat, May 16 · 12:00 PM → Sun, May 17 · 6:00 PM EDT'
 * ```
 */
export function formatEventDateRange(
  start: Date | string,
  end: Date | string | null | undefined,
  timezone: string = 'UTC',
): string {
  const startObj = toValidDate(start);
  if (!startObj) {
    return '';
  }

  const tz = isValidTimezone(timezone) ? timezone : 'UTC';

  // No usable end → single instant. `formatEventDateTime` already owns the
  // exact same grammar, so reuse it rather than re-deriving the format here.
  const endObj = end == null ? null : toValidDate(end);
  if (!endObj) {
    return formatEventDateTime(startObj, tz);
  }

  try {
    const startSide = formatInTimeZone(startObj, tz, 'EEE, MMM d · h:mm a');
    const startLocalDay = formatInTimeZone(startObj, tz, 'yyyy-MM-dd');
    const endLocalDay = formatInTimeZone(endObj, tz, 'yyyy-MM-dd');
    const endTz = formatInTimeZone(endObj, tz, 'zzz');

    if (startLocalDay === endLocalDay) {
      // Same local day — collapse to a single date + time range so we don't
      // repeat the (identical) calendar date on both sides.
      const endTime = formatInTimeZone(endObj, tz, 'h:mm a');
      return `${startSide} – ${endTime} ${endTz}`;
    }

    // Multi-day — explicit two-sided range. Both sides carry weekday + date
    // so "spans these days" is unambiguous (the WEB-293 fix for the opaque
    // `→ MAY 17` arrow notation).
    const endSide = formatInTimeZone(endObj, tz, 'EEE, MMM d · h:mm a');
    return `${startSide} → ${endSide} ${endTz}`;
  } catch {
    return '';
  }
}

/**
 * The single server-authoritative description of *when* an event happens.
 *
 * Returned by {@link formatEventWhen}, which is the ONE place every render
 * surface (detail hero, slug-routed page, category listing, Discover card,
 * map hover card) must go through to humanize an event's start/end. Before
 * PIP-98 each surface formatted dates independently — some passed the venue
 * timezone, some fell back to `Intl` viewer-locale, some sliced the UTC
 * calendar date. The result: "STING 3.0 Tour" rendered `MON MAY 18 8:00 PM`
 * on the detail hero but `MAY 19` on the slug + category listing (the slug
 * suffix and the listing both projected the `2026-05-19T00:00:00Z` instant
 * onto the UTC calendar day while the hero projected it onto the
 * America/New_York day). And a date-only source whose `startsAt` collapsed
 * to fake midnight rendered a confident wrong time ("ACC Baseball
 * Championship" at 5 AM).
 *
 * Every field below is derived from the venue's IANA `timezone` — never the
 * viewer's locale — so the same event renders identically on every surface
 * regardless of where the reader's browser thinks it is.
 */
export interface EventWhen {
  /**
   * Venue-local calendar date as `YYYY-MM-DD`. This is the canonical date
   * the slug suffix and every date label MUST agree on (PIP-98 AC #3).
   */
  localDate: string;
  /**
   * Humanized date line — `"Mon, May 18"`. Always present (a date-only
   * event still has a real calendar day; only the *time* is unknown).
   */
  dateLine: string;
  /**
   * Humanized time line — `"8:00 PM EDT"`. `null` when {@link isTimeKnown}
   * is `false` so callers render a "Time TBA" affordance instead of a
   * fabricated clock value (PIP-98 AC #2).
   */
  timeLine: string | null;
  /**
   * Single-line label suitable for cards/rails. `"Mon, May 18 · 8:00 PM
   * EDT"` when the time is known, `"Mon, May 18 · Time TBA"` when not.
   */
  label: string;
  /**
   * Whether the start time is a real wall-clock value. Mirrors the
   * server-authoritative `Event.startTimeKnown` column. `false` =>
   * `timeLine` is `null` and `label` carries the "Time TBA" suffix.
   */
  isTimeKnown: boolean;
}

/** Copy rendered in place of a time when the start time is unknown. */
export const TIME_TBA_LABEL = 'Time TBA' as const;

/**
 * The ONE server-authoritative event date/time formatter (PIP-98).
 *
 * Projects the canonical `start` instant onto the venue's IANA `timezone`
 * and returns every string a surface needs ({@link EventWhen}). Honors the
 * server's `startTimeKnown` signal: when `false` (date-only source, or the
 * fake-midnight-UTC signature the ingest pipeline could not resolve to a
 * real time) the time is suppressed rather than fabricated.
 *
 * Invariants:
 * - `localDate`, `dateLine`, and `label` are ALWAYS computed in `timezone`
 *   — never the viewer's locale. This is what keeps the slug suffix, the
 *   category listing, the Discover card, and the detail hero in lockstep.
 * - Unparseable `start` → all string fields `''` / `null` (except `label`
 *   which is `''`) so callers can omit the row rather than leak raw ISO.
 * - Invalid/empty `timezone` → falls back to `UTC` (a valid `start` still
 *   produces a coherent label, just rendered in UTC).
 * - `startTimeKnown` defaults to `true` so existing callers that have no
 *   honest-signal column keep their prior behavior; pass `false`
 *   explicitly to suppress the time.
 *
 * @param start - Event start as a `Date` or ISO 8601 string
 * @param timezone - Server-authoritative IANA timezone (e.g.
 *   `America/New_York`)
 * @param startTimeKnown - The `Event.startTimeKnown` signal; `false`
 *   suppresses the time line. Defaults to `true`.
 *
 * @example
 * ```typescript
 * // STING 3.0 Tour — 8 PM EDT on May 18 (the instant is 2026-05-19T00:00:00Z)
 * formatEventWhen('2026-05-19T00:00:00Z', 'America/New_York', true);
 * // → { localDate: '2026-05-18', dateLine: 'Mon, May 18',
 * //     timeLine: '8:00 PM EDT', label: 'Mon, May 18 · 8:00 PM EDT',
 * //     isTimeKnown: true }
 *
 * // Date-only source — startsAt collapsed to fake midnight UTC
 * formatEventWhen('2026-05-20T00:00:00Z', 'America/New_York', false);
 * // → { localDate: '2026-05-19', dateLine: 'Mon, May 19',
 * //     timeLine: null, label: 'Mon, May 19 · Time TBA',
 * //     isTimeKnown: false }
 * ```
 */
export function formatEventWhen(
  start: Date | string,
  timezone: string = 'UTC',
  startTimeKnown: boolean = true,
): EventWhen {
  const empty: EventWhen = {
    localDate: '',
    dateLine: '',
    timeLine: null,
    label: '',
    isTimeKnown: false,
  };

  const startObj = toValidDate(start);
  if (!startObj) {
    return empty;
  }

  const tz = isValidTimezone(timezone) ? timezone : 'UTC';

  try {
    const localDate = formatInTimeZone(startObj, tz, 'yyyy-MM-dd');
    const dateLine = formatInTimeZone(startObj, tz, 'EEE, MMM d');

    if (!startTimeKnown) {
      return {
        localDate,
        dateLine,
        timeLine: null,
        label: `${dateLine} · ${TIME_TBA_LABEL}`,
        isTimeKnown: false,
      };
    }

    const timeLine = formatInTimeZone(startObj, tz, 'h:mm a zzz');
    return {
      localDate,
      dateLine,
      timeLine,
      label: `${dateLine} · ${timeLine}`,
      isTimeKnown: true,
    };
  } catch {
    return empty;
  }
}

/**
 * Recurring event timezone handling
 * Ensures recurring events respect timezone changes and DST
 */
export interface RecurringEventOptions {
  /** Base event start time in UTC */
  baseStartTime: Date;
  /** Base event end time in UTC (optional) */
  baseEndTime?: Date;
  /** Timezone for the event */
  timezone: string;
  /** Recurrence pattern (daily, weekly, monthly) */
  pattern: 'daily' | 'weekly' | 'monthly';
  /** Number of occurrences to generate */
  count: number;
}

/**
 * Generate occurrence times for recurring events
 * Handles timezone and DST changes correctly
 *
 * @param options - Recurring event configuration
 * @returns Array of occurrence time ranges
 */
export function generateRecurringEventTimes(
  options: RecurringEventOptions,
): Array<{ start: Date; end?: Date; formats: EventTimeFormats }> {
  const { baseStartTime, baseEndTime, timezone, pattern, count } = options;
  const occurrences: Array<{ start: Date; end?: Date; formats: EventTimeFormats }> = [];

  for (let i = 0; i < count; i++) {
    let occurrenceStart: Date;
    let occurrenceEnd: Date | undefined;

    // Calculate occurrence date based on pattern
    switch (pattern) {
      case 'daily':
        occurrenceStart = new Date(baseStartTime);
        occurrenceStart.setDate(baseStartTime.getDate() + i);
        if (baseEndTime) {
          occurrenceEnd = new Date(baseEndTime);
          occurrenceEnd.setDate(baseEndTime.getDate() + i);
        }
        break;

      case 'weekly':
        occurrenceStart = new Date(baseStartTime);
        occurrenceStart.setDate(baseStartTime.getDate() + i * 7);
        if (baseEndTime) {
          occurrenceEnd = new Date(baseEndTime);
          occurrenceEnd.setDate(baseEndTime.getDate() + i * 7);
        }
        break;

      case 'monthly':
        occurrenceStart = new Date(baseStartTime);
        occurrenceStart.setMonth(baseStartTime.getMonth() + i);
        if (baseEndTime) {
          occurrenceEnd = new Date(baseEndTime);
          occurrenceEnd.setMonth(baseEndTime.getMonth() + i);
        }
        break;
    }

    // Format the occurrence with timezone awareness
    const formats = formatEventTimes(occurrenceStart, occurrenceEnd, timezone);

    occurrences.push({
      start: occurrenceStart,
      end: occurrenceEnd,
      formats,
    });
  }

  return occurrences;
}
